/**
 * Shared validation + time-slot helpers for provider weekly availability UI.
 */
import {
  DEFAULT_PROVIDER_AVAILABILITY_SLOT,
  PROVIDER_AVAILABILITY_UI_DAYS,
  type ProviderAvailabilitySlot,
  type ProviderWeeklyAvailability,
} from './providerAvailabilityTypes';

export const TIME_SLOT_LABELS: string[] = [];
for (let h = 7; h <= 21; h++) {
  for (const m of [0, 15, 30, 45]) {
    if (h === 21 && m > 0) break;
    const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const period = h >= 12 ? 'PM' : 'AM';
    TIME_SLOT_LABELS.push(`${hour12}:${m.toString().padStart(2, '0')} ${period}`);
  }
}

export const TIME_SLOT_OPTIONS = TIME_SLOT_LABELS.map((label) => ({ value: label, label }));

export function timeSlotOptionsInWindow(
  openingMinutes: number,
  closingMinutes: number,
  kind: 'start' | 'end',
): { value: string; label: string }[] {
  return TIME_SLOT_OPTIONS.filter((opt) => {
    const mins = timeLabelToMinutes(opt.value);
    if (mins == null) return false;
    return kind === 'start'
      ? mins >= openingMinutes && mins < closingMinutes
      : mins > openingMinutes && mins <= closingMinutes;
  });
}

/** True when the 15-minute grid has a start and end inside the window with start < end. */
export function dayWindowHasSelectableRange(openingMinutes: number, closingMinutes: number): boolean {
  const start = timeSlotOptionsInWindow(openingMinutes, closingMinutes, 'start');
  const end = timeSlotOptionsInWindow(openingMinutes, closingMinutes, 'end');
  const startM = start[0] ? timeLabelToMinutes(start[0].value) : null;
  const endM = end.length > 0 ? timeLabelToMinutes(end[end.length - 1].value) : null;
  return startM != null && endM != null && startM < endM;
}

export function parseTimeLabel12h(label: string): { hour: number; minute: number } | null {
  const match = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;
  const period = match[3].toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return { hour, minute };
}

function minutesToTimeLabel(totalMinutes: number): string {
  const snapped = Math.round(totalMinutes / 15) * 15;
  const hour = Math.floor(snapped / 60) % 24;
  const minute = snapped % 60;
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const period = hour < 12 ? 'AM' : 'PM';
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
}

function timeLabelToMinutes(label: string): number | null {
  const parsed = parseTimeLabel12h(label);
  if (!parsed) return null;
  return parsed.hour * 60 + parsed.minute;
}

export function isInvalidSlotTimeRange(slot: ProviderAvailabilitySlot): boolean {
  if (!slot.start || !slot.end) return false;
  const startM = timeLabelToMinutes(slot.start);
  const endM = timeLabelToMinutes(slot.end);
  if (startM === null || endM === null) return false;
  return endM <= startM;
}

function mergeSlots(slots: ProviderAvailabilitySlot[]): ProviderAvailabilitySlot[] {
  const complete = slots.filter((s) => s.start && s.end);
  if (complete.length <= 1) return complete.map((s) => ({ ...s }));
  const inMinutes = complete
    .map((s) => {
      const startP = parseTimeLabel12h(s.start);
      const endP = parseTimeLabel12h(s.end);
      if (!startP || !endP) return null;
      return {
        start: startP.hour * 60 + startP.minute,
        end: endP.hour * 60 + endP.minute,
      };
    })
    .filter((x): x is { start: number; end: number } => x !== null);
  if (inMinutes.length === 0) return [];
  inMinutes.sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  for (const seg of inMinutes) {
    if (merged.length > 0 && seg.start <= merged[merged.length - 1].end) {
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, seg.end);
    } else {
      merged.push({ start: seg.start, end: seg.end });
    }
  }
  return merged.map((m) => ({
    start: minutesToTimeLabel(m.start),
    end: minutesToTimeLabel(m.end),
  }));
}

export function mergeWeeklyAvailability(weekly: ProviderWeeklyAvailability): ProviderWeeklyAvailability {
  const result: ProviderWeeklyAvailability = {};
  for (const day of PROVIDER_AVAILABILITY_UI_DAYS) {
    const d = weekly[day];
    if (!d) continue;
    result[day] = {
      enabled: d.enabled,
      slots: d.slots?.length ? mergeSlots(d.slots) : d.slots,
    };
  }
  return result;
}

export function getOverlappingSlotIndicesForDay(slots: ProviderAvailabilitySlot[]): number[] {
  if (slots.length <= 1) return [];
  const toMinutes = (s: ProviderAvailabilitySlot) => {
    const startP = parseTimeLabel12h(s.start);
    const endP = parseTimeLabel12h(s.end);
    if (!startP || !endP) return null;
    return { start: startP.hour * 60 + startP.minute, end: endP.hour * 60 + endP.minute };
  };
  const ranges = slots.map(toMinutes);
  const overlapping = new Set<number>();
  for (let i = 0; i < ranges.length; i++) {
    if (ranges[i] === null) continue;
    for (let j = i + 1; j < ranges.length; j++) {
      if (ranges[j] === null) continue;
      const a = ranges[i]!;
      const b = ranges[j]!;
      if (a.start < b.end && b.start < a.end) {
        overlapping.add(i);
        overlapping.add(j);
      }
    }
  }
  return [...overlapping];
}

export function hasOverlappingSlotsInWeekly(weekly: ProviderWeeklyAvailability): boolean {
  for (const day of PROVIDER_AVAILABILITY_UI_DAYS) {
    const slots = weekly[day]?.slots ?? [];
    if (getOverlappingSlotIndicesForDay(slots).length > 0) return true;
  }
  return false;
}

export function hasEmptySlotsInWeekly(weekly: ProviderWeeklyAvailability): boolean {
  for (const day of PROVIDER_AVAILABILITY_UI_DAYS) {
    const d = weekly[day];
    if (!d?.enabled) continue;
    const slots = d.slots ?? [];
    if (slots.length === 0 || slots.some((s) => !s.start || !s.end)) return true;
  }
  return false;
}

export function hasEndNotAfterStartInWeekly(weekly: ProviderWeeklyAvailability): boolean {
  for (const day of PROVIDER_AVAILABILITY_UI_DAYS) {
    const d = weekly[day];
    if (!d?.enabled) continue;
    for (const slot of d.slots) {
      if (isInvalidSlotTimeRange(slot)) return true;
    }
  }
  return false;
}

export function hasAtLeastOneWorkingDay(weekly: ProviderWeeklyAvailability): boolean {
  for (const day of PROVIDER_AVAILABILITY_UI_DAYS) {
    const d = weekly[day];
    if (!d?.enabled) continue;
    const slot = d.slots?.[0];
    if (!slot?.start || !slot?.end) continue;
    if (isInvalidSlotTimeRange(slot)) continue;
    return true;
  }
  return false;
}

export function getProviderWeeklyAvailabilityUiDisableReason(
  weekly: ProviderWeeklyAvailability,
): string | undefined {
  if (hasEndNotAfterStartInWeekly(weekly)) return 'End time must be after start time';
  if (hasOverlappingSlotsInWeekly(weekly)) return "Time slots shouldn't overlap";
  if (hasEmptySlotsInWeekly(weekly)) return 'Fill in all time slots';
  if (!hasAtLeastOneWorkingDay(weekly)) return 'Select at least one working day';
  return undefined;
}

export { DEFAULT_PROVIDER_AVAILABILITY_SLOT, PROVIDER_AVAILABILITY_UI_DAYS };
